import { sagaRoot } from "./root";
import { call, put, takeLatest } from "redux-saga/effects";
import { LOGIN, SIGN_UP, UPDATE_AUTH } from "../constant";
import { POST } from "../constant/apiMethods";
import Labels from "../services/apiLabels";
import config from "../config";
import { syncDealersSessionFromHomeLogin } from "../utils/dealerSessionSync";
import { isDealerUser, pickUserType } from "../utils/userTypes";
import { sanitizeImageUrl } from "../utils/sanitizeImageUrl";
import { markOnboardingTourPending } from "../utils/onboardingTour";

function looksLikeUser(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  return Boolean(
    obj.first_name != null ||
      obj.firstname != null ||
      obj.last_name != null ||
      obj.lastname != null ||
      obj.email != null ||
      obj.username != null ||
      obj.id != null ||
      obj.user_id != null ||
      obj.pk != null
  );
}

/** Flatten login/register API payloads into a single auth-shaped object. */
export function flattenAuthResponse(response = {}) {
  const root = response && typeof response === "object" ? response : {};
  const data =
    root.data && typeof root.data === "object" && !Array.isArray(root.data)
      ? root.data
      : {};
  const nestedData =
    data.data && typeof data.data === "object" && !Array.isArray(data.data)
      ? data.data
      : {};

  const user =
    (looksLikeUser(root.user) && root.user) ||
    (looksLikeUser(data.user) && data.user) ||
    (looksLikeUser(nestedData.user) && nestedData.user) ||
    (looksLikeUser(nestedData) && nestedData) ||
    (looksLikeUser(data) && data) ||
    (looksLikeUser(root) && root) ||
    {};

  const token =
    data.token ||
    root.token ||
    user.token ||
    data.key ||
    root.key ||
    "";

  const referral =
    user.referral_code ||
    user.code ||
    data.referral_code ||
    data.code ||
    root.referral_code ||
    root.code ||
    "";

  return {
    ...data,
    ...user,
    token,
    user,
    id:
      user.id ||
      user.user_id ||
      user.pk ||
      data.id ||
      data.user_id ||
      root.id ||
      root.user_id ||
      "",
    email: user.email || data.email || root.email || "",
    username: user.username || data.username || root.username || "",
    firstname:
      user.firstname ||
      user.first_name ||
      data.firstname ||
      data.first_name ||
      root.firstname ||
      root.first_name ||
      "",
    lastname:
      user.lastname ||
      user.last_name ||
      data.lastname ||
      data.last_name ||
      root.lastname ||
      root.last_name ||
      "",
    first_name:
      user.first_name ||
      user.firstname ||
      data.first_name ||
      data.firstname ||
      "",
    last_name:
      user.last_name ||
      user.lastname ||
      data.last_name ||
      data.lastname ||
      "",
    referral_code: referral,
    code: referral || user.code || data.code || "",
  };
}

function pickLoginUserId(data = {}) {
  const flat = flattenAuthResponse({ data });
  return (
    flat.id ||
    data.id ||
    data.user_id ||
    data.pk ||
    data.user?.id ||
    data.user?.user_id ||
    data.data?.id ||
    data.data?.user_id ||
    data.data?.user?.id ||
    ""
  );
}

function pickImage(source = {}, nested = {}) {
  const raw =
    source.image ||
    source.image_url ||
    source.profile_image ||
    source.profile_image_url ||
    source.avatar ||
    source.avatar_url ||
    nested.image ||
    nested.image_url ||
    nested.profile_image ||
    nested.profile_image_url ||
    nested.avatar ||
    nested.avatar_url ||
    "";
  return sanitizeImageUrl(raw) || raw || "";
}

/** Map register/login form body into auth-shaped fields for immediate UI. */
function authFieldsFromFormBody(body = {}) {
  const first = body.first_name || body.firstname || "";
  const last = body.last_name || body.lastname || "";
  return {
    firstname: first,
    lastname: last,
    first_name: first,
    last_name: last,
    email: body.email || "",
    username: body.username || "",
    type: body.type != null ? body.type : undefined,
  };
}

function authPatchFromUser(user = {}, fallback = {}) {
  const first =
    user.firstname ||
    user.first_name ||
    fallback.firstname ||
    fallback.first_name ||
    "";
  const last =
    user.lastname ||
    user.last_name ||
    fallback.lastname ||
    fallback.last_name ||
    "";
  const patch = {
    email: user.email || fallback.email || "",
    firstname: first,
    lastname: last,
    first_name: first,
    last_name: last,
    username: user.username || fallback.username || "",
    id: user.id || user.user_id || user.pk || fallback.id || "",
    image: pickImage(user, fallback),
    mobile_number:
      user.mobile_number ||
      user.phone ||
      user.mobile ||
      fallback.mobile_number ||
      "",
    postcode:
      user.postcode ||
      user.postal_code ||
      fallback.postcode ||
      "",
    referral_code:
      user.referral_code ||
      user.code ||
      fallback.referral_code ||
      fallback.code ||
      "",
    credits:
      user.credits != null
        ? user.credits
        : user.credit != null
          ? user.credit
          : fallback.credits != null
            ? fallback.credits
            : null,
    last_evaluation:
      user.last_evaluation ||
      user.lastEvaluation ||
      fallback.last_evaluation ||
      "",
  };

  const resolvedType = pickUserType(user, fallback);
  if (resolvedType != null) patch.type = resolvedType;

  const cleaned = {};
  Object.keys(patch).forEach((key) => {
    const value = patch[key];
    if (value == null || value === "") return;
    cleaned[key] = value;
  });
  return cleaned;
}

function parseProfileResponse(response) {
  if (!response || typeof response !== "object") return null;
  // Ignore explicit API errors
  if (response.message?.type === "error") return null;

  const payload = response.data || response || {};
  const user =
    (looksLikeUser(payload?.user) && payload.user) ||
    (looksLikeUser(payload?.data?.user) && payload.data.user) ||
    (looksLikeUser(payload?.data) && payload.data) ||
    (looksLikeUser(payload) && payload) ||
    null;

  return user ? { user, payload } : null;
}

function* fetchProfileUser(token, userId) {
  if (!token || !userId) return null;
  const headers = {
    Authorization: `Token ${token}`,
    Accept: "application/json",
  };
  const urls = [
    `${config.baseUrl}${Labels.getupdateprofile}${userId}/`,
    `${config.baseUrl}${Labels.getupdateprofile}${userId}`,
  ];

  for (let i = 0; i < urls.length; i += 1) {
    try {
      const parsed = yield call(() =>
        fetch(urls[i], { method: "GET", headers })
          .then(async (res) => {
            let json = null;
            try {
              json = await res.json();
            } catch {
              json = null;
            }
            if (!res.ok && !json) return null;
            return parseProfileResponse(json);
          })
      );
      if (parsed?.user) return parsed;
    } catch (e) {
      /* try next url */
    }
  }
  return null;
}

/**
 * Ensure header/account have name/email/etc after login or signup.
 */
function* hydrateAuthProfile(loginData = {}, formFallback = {}) {
  const flat = flattenAuthResponse({ data: loginData });
  const token = flat.token || loginData.token;
  const userId = pickLoginUserId(flat) || pickLoginUserId(loginData) || formFallback.id || "";

  // Apply known fields immediately so the UI is not blank.
  const optimistic = authPatchFromUser(flat, formFallback);
  if (Object.keys(optimistic).length) {
    yield put({ type: UPDATE_AUTH, payload: optimistic });
  }

  const profile = yield call(fetchProfileUser, token, userId);
  if (!profile?.user) {
    return pickUserType(flat, loginData, loginData?.user);
  }

  const patch = authPatchFromUser(profile.user, {
    ...formFallback,
    ...flat,
  });
  const userType = pickUserType(profile.user, profile.payload, flat, loginData);
  if (userType != null) patch.type = userType;

  if (Object.keys(patch).length) {
    yield put({ type: UPDATE_AUTH, payload: patch });
  }
  return userType;
}

// login
export function* loginSaga(payload) {
  const response = yield call(sagaRoot, payload);
  if (response?.message?.type === "success") {
    const requestBody = payload.payload?.data || {};
    const formFallback = {
      ...authFieldsFromFormBody(requestBody),
      ...(payload.payload?.profileFallback || {}),
      // Password login always has username — keep it for the header
      username:
        requestBody.username ||
        payload.payload?.profileFallback?.username ||
        "",
    };

    const loginData = flattenAuthResponse(response);
    // Prefer login/profile type; fall back to signup form type (dealer=1).
    const loginType = pickUserType(loginData, formFallback) ?? formFallback.type;
    // Re-store flattened session (sagaRoot may have saved a nested/empty shape)
    yield put({
      type: `${LOGIN}_SUCCESS`,
      payload: {
        ...loginData,
        ...(loginType != null ? { type: loginType } : {}),
      },
    });

    const userType = yield call(hydrateAuthProfile, loginData, formFallback);

    const withType = {
      ...loginData,
      ...formFallback,
      type: userType ?? loginType ?? formFallback.type,
    };
    // Persist account type when hydrate/profile still omit it.
    if (withType.type != null && withType.type !== loginType) {
      yield put({ type: UPDATE_AUTH, payload: { type: withType.type } });
    }
    if (isDealerUser(withType) || isDealerUser(withType.user || {})) {
      syncDealersSessionFromHomeLogin(withType);
    }

    const callBack = payload.payload.callBack;
    if (typeof callBack === "function") {
      callBack({ ...response, data: withType });
    }
  }
}

/**
 * Register, then always password-login when possible.
 * Register returns token+user but omits `id`; login returns id/credits/referral_code
 * which profile, credits, and referral panels need.
 */
export function* signUpSaga(payload) {
  const response = yield call(sagaRoot, payload);
  if (response?.message?.type !== "success") return;

  const body = payload.payload?.data || {};
  const callBack = payload.payload?.callBack;
  // First-login tour after auto-login (client or dealer signup).
  markOnboardingTourPending(body.type ?? body);
  const formFallback = authFieldsFromFormBody(body);
  const signupData = flattenAuthResponse(response);
  const referralFromRegister =
    signupData.referral_code ||
    signupData.code ||
    signupData.user?.code ||
    signupData.user?.referral_code ||
    "";

  // Prefer login after register whenever we have credentials.
  if (body.username && body.password) {
    yield call(loginSaga, {
      payload: {
        type: LOGIN,
        method: POST,
        label: Labels.login,
        hideOverlayLoader: true,
        data: {
          username: body.username,
          password: body.password,
        },
        profileFallback: {
          ...formFallback,
          ...(referralFromRegister
            ? { referral_code: referralFromRegister, code: referralFromRegister }
            : {}),
        },
        callBack,
      },
    });
    return;
  }

  // Token-only register (e.g. some social flows) — no password to exchange.
  if (signupData.token) {
    const withForm = {
      ...formFallback,
      ...signupData,
      firstname: signupData.firstname || formFallback.firstname || "",
      lastname: signupData.lastname || formFallback.lastname || "",
      email: signupData.email || formFallback.email || "",
      username: signupData.username || formFallback.username || "",
      referral_code: referralFromRegister || signupData.referral_code || "",
      code: referralFromRegister || signupData.code || "",
      type: signupData.type ?? formFallback.type,
    };

    yield put({ type: `${LOGIN}_SUCCESS`, payload: withForm });

    const userType = yield call(hydrateAuthProfile, withForm, formFallback);
    const withType = {
      ...withForm,
      type: userType ?? withForm.type,
    };

    if (isDealerUser(withType) || isDealerUser(withType.user || {})) {
      syncDealersSessionFromHomeLogin(withType);
    }
    if (typeof callBack === "function") {
      callBack({ ...response, data: withType });
    }
  }
}

export default function* authWatcher() {
  yield takeLatest(LOGIN, loginSaga);
  yield takeLatest(SIGN_UP, signUpSaga);
}
